import React, { useState, useContext, useEffect, useRef } from 'react'
import {
    Badge,
    Button,
    FormControl,
    List,
    ListItem,
    makeStyles,
    MenuItem,
    Select,
    Typography,
    useTheme,
} from '@material-ui/core'
import Popover, { PopoverOrigin } from '@material-ui/core/Popover'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import CircleIcon from '@material-ui/icons/Circle'
import MessageIcon from '@material-ui/icons/Message'
import useMuseStyles from '../../themes/useMuseStyles'
import clsx from 'clsx'
import { UserContext, UserContextType } from '../../UserContext'
import { CommunityMessagesPageMode } from './CommunityMessagesPage'
import CommunityChatbox from './CommunityChatbox'
import { AppContext, AppContextType } from '../../AppContext'
import { useCommunityFeature } from '../../services/useCommunityFeature'
import { formatTimestamp } from '../../utils'
import { RoomContext, RoomContextType } from '../../RoomContext'

const borderColor = '#3F3F3F'

function CommunityMessagesChatPage({
    setPage,
    communityFeature,
}: {
    setPage: React.Dispatch<React.SetStateAction<CommunityMessagesPageMode>>
    communityFeature: ReturnType<typeof useCommunityFeature>
}) {
    const gclasses = useMuseStyles(useTheme())

    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const [isOptionsMenuOpen, set_isOptionsMenuOpen] = useState<boolean>(false)

    // const currentMessageGroup = state.community.messageGroups.filter(g => g._id === state.community.selectedMessageGroupId)[0] || null

    useEffect(() => {
        communityFeature.getMyMessageGroups()

        return () => {
            dispatch({
                type: 'update_community_state',
                value: {
                    selectedMessageGroupId: null,
                },
            })
        }
    }, [])

    useEffect(() => {
        if (state.community.messageGroups && state.community.messageGroups.length > 0) {
            dispatch({
                type: 'update_community_state',
                value: { selectedMessageGroupId: state.community.messageGroups[0]._id },
            })
        } else {
            dispatch({
                type: 'update_community_state',
                value: {
                    selectedMessageGroupId: null,
                },
            })
        }
    }, [state.community.messageGroups])

    useEffect(() => {
        if (communityFeature.deleteMessageGroupComplete) {
            communityFeature.getMyMessageGroups()
        }
    }, [communityFeature.deleteMessageGroupComplete])

    useEffect(() => {
        // Clear unread messages for the selected message group.
        if (state.community.selectedMessageGroupId) {
            const groupIdToClear = state.community.selectedMessageGroupId
            const messageGroupUnreadMessagesPerGroup_next = new Map(state.community.messageGroupUnreadMessagesPerGroup)
            messageGroupUnreadMessagesPerGroup_next.delete(groupIdToClear)

            dispatch({
                type: 'update_community_state',
                value: {
                    messageGroupUnreadMessagesPerGroup: messageGroupUnreadMessagesPerGroup_next,
                },
            })
        }
    }, [state.community.selectedMessageGroupId])

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <div
                    style={{
                        display: 'flex',
                        width: 'fit-content',
                        flexDirection: 'column',
                        alignItems: 'center',
                        marginBottom: 8,
                    }}
                >
                    <Button
                        variant="outlined"
                        color="primary"
                        style={{ marginTop: '5px', width: 40, height: 40 }}
                        className={clsx(gclasses.circleButton, gclasses.buttonSmall, gclasses.buttonSvgIconSmall)}
                        classes={{
                            label: gclasses.primaryColor,
                            disabled: gclasses.circleButtonDisabled,
                        }}
                        onClick={() => {
                            setPage('new_messages')
                        }}
                    >
                        <MessageIcon />
                    </Button>
                    <Typography variant="body1" className={gclasses.textSmaller} style={{ marginTop: 5 }}>
                        New Message
                    </Typography>
                </div>
            </div>
            <div style={{ borderTop: 'solid 1px ' + borderColor, display: 'flex', flexGrow: 2 }}>
                <div
                    id="messages-panel-left"
                    style={{ borderRight: 'solid 1px ' + borderColor, width: 220, maxHeight: 560, overflowY: 'auto' }}
                >
                    <List className={gclasses.navMenu2} component="nav">
                        {state.community.messageGroups.map((group) => {
                            const peerMembers = group.members.filter((member) => member.userUrn !== state.userUrn)
                            const peerUsernames = peerMembers.map((m) => m.username)
                            const unreadCount = state.community.messageGroupUnreadMessagesPerGroup.get(group._id) || 0

                            // is active if any one of the members is online.
                            let isActive = false
                            for (let i = 0; i < peerMembers.length; ++i) {
                                if (peerMembers[i].activeStatus === 'active') {
                                    isActive = true
                                    break
                                }
                            }

                            const activeIconStyle = isActive ? { color: 'limegreen' } : { color: 'gray' }

                            return (
                                <Badge
                                    className={gclasses.badgeCommunityMessagesNav}
                                    style={{ width: '100%' }}
                                    badgeContent={unreadCount}
                                    color="primary"
                                    invisible={unreadCount <= 0}
                                    overlap="rectangular"
                                >
                                    <ListItem
                                        key={group._id}
                                        className={clsx(
                                            group._id === state.community.selectedMessageGroupId &&
                                                gclasses.navMenu2Selected
                                        )}
                                        style={{ paddingLeft: 10 }}
                                        onClick={() => {
                                            dispatch({
                                                type: 'update_community_state',
                                                value: {
                                                    selectedMessageGroupId: group._id,
                                                },
                                            })
                                        }}
                                        button
                                    >
                                        <div style={{ flexGrow: 2, overflowX: 'auto' }}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <CircleIcon style={{ width: 8, marginRight: 5, ...activeIconStyle }} />
                                                <Typography className={gclasses.textEllipsis} variant="body1">
                                                    {peerUsernames[0]}
                                                </Typography>
                                            </div>
                                            {peerUsernames.length > 1 && (
                                                <Typography
                                                    className={clsx(
                                                        gclasses.textEllipsis,
                                                        gclasses.textSmaller80,
                                                        gclasses.buttonColorGrayout
                                                    )}
                                                    style={{ marginLeft: 12, marginTop: -3 }}
                                                    variant="body1"
                                                >
                                                    {`+ ${peerUsernames.length - 1} others`}
                                                </Typography>
                                            )}
                                        </div>
                                        <Typography
                                            variant="body1"
                                            className={clsx(gclasses.textSmaller70, gclasses.buttonColorGrayout)}
                                            style={{ textAlign: 'right', margin: 'auto' }}
                                        >
                                            {formatTimestamp(new Date(group.updatedAt), { concise: true })}
                                        </Typography>
                                    </ListItem>
                                </Badge>
                            )
                        })}
                    </List>
                </div>
                <div id="messages-panel-right" style={{ flexGrow: 2, display: 'flex', flexDirection: 'column' }}>
                    {state.community.selectedMessageGroupId ? (
                        <>
                            <div style={{ borderBottom: 'solid 1px ' + borderColor, display: 'flex', height: 40 }}>
                                <div
                                    style={{
                                        flexGrow: 2,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <AbstractInviteToSessionInteractable
                                        id="communitymessageschatpageinvitesession-tooltip"
                                        component={({ isButtonDisabled, onSendInviteClicked }) => (
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                style={{ height: 26, margin: 'auto', textTransform: 'none' }}
                                                className={clsx(
                                                    gclasses.roundedMuseButton,
                                                    gclasses.buttonSmall,
                                                    gclasses.buttonSvgIconSmall,
                                                    isButtonDisabled && gclasses.circleButtonDisabled
                                                )}
                                                classes={{
                                                    label: gclasses.roundedMuseButtonLabel,
                                                }}
                                                onClick={() => onSendInviteClicked()}
                                                disabled={isButtonDisabled}
                                            >
                                                Invite to Session
                                            </Button>
                                        )}
                                        communityFeature={communityFeature}
                                    />
                                </div>

                                <Button onClick={() => set_isOptionsMenuOpen(true)}>
                                    <MoreHorizIcon />
                                </Button>
                                <FormControl className={gclasses.formControlHidden}>
                                    <RoundedSelect
                                        id="community-message-group-options"
                                        style={{ float: 'right' }}
                                        open={isOptionsMenuOpen}
                                        onClose={() => set_isOptionsMenuOpen(false)}
                                        onOpen={() => {}}
                                        value={''}
                                        onChange={(event: any) => {
                                            set_isOptionsMenuOpen(false)
                                            const value = event.target.value
                                        }}
                                    >
                                        <MenuItem
                                            value="delete-conversation"
                                            style={{ color: '#C33E36' }}
                                            onClick={() => {
                                                if (state.community?.selectedMessageGroupId) {
                                                    communityFeature.deleteMessageGroup(
                                                        state.community.selectedMessageGroupId
                                                    )
                                                } else {
                                                    appDispatch({
                                                        type: 'root_snackbar_history_append',
                                                        value: {
                                                            severity: 'error',
                                                            message: 'Failed to delete message group: id is null.',
                                                        },
                                                    })
                                                }
                                            }}
                                        >
                                            Delete Conversation
                                        </MenuItem>
                                    </RoundedSelect>
                                </FormControl>
                            </div>
                            <div id="message-message-pane" style={{ flexGrow: 2 }}>
                                {state.userUrn && state.community.selectedMessageGroupId && (
                                    <CommunityChatbox
                                        key={`message-message-pane_${state.community.selectedMessageGroupId}`}
                                        userUrn={state.userUrn}
                                        groupUrn={state.community.selectedMessageGroupId}
                                    />
                                )}
                            </div>
                        </>
                    ) : (
                        <div></div>
                    )}
                </div>
            </div>
        </div>
    )
}

export function AbstractInviteToSessionInteractable({
    id,
    invite,
    component,
    communityFeature,
}: {
    id: string
    invite?: { userUrn: string; username: string }
    component: ({
        isButtonDisabled,
        onSendInviteClicked,
    }: {
        isButtonDisabled: boolean
        onSendInviteClicked: () => void
    }) => React.ReactNode
    communityFeature: ReturnType<typeof useCommunityFeature>
}) {
    const gclasses = useMuseStyles(useTheme())

    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const { state } = useContext(UserContext) as UserContextType

    const getPeerInfo = () => {
        // If `invite` is set, then we use that.
        if (invite) {
            return { peerUrns: [invite.userUrn], peerUsernames: [invite.username] }
        } else {
            // Infer from current selected message group.
            const currentMessageGroup =
                state.community.messageGroups.filter((g) => g._id === state.community.selectedMessageGroupId)[0] || null
            if (currentMessageGroup) {
                const peerUrns = (currentMessageGroup.members || [])
                    .map((m) => m.userUrn)
                    .filter((userUrn) => userUrn !== state.userUrn) // don't include self
                const peerUsernames = (currentMessageGroup.members || [])
                    .map((m) => m.username)
                    .filter((username) => username !== state.profile?.username) // don't include self
                return { peerUrns, peerUsernames }
            } else {
                return { peerUrns: [], peerUsernames: [] }
            }
        }
    }

    const { peerUrns, peerUsernames } = getPeerInfo()

    const roomUrn = roomState.roomUrn || roomState.preRoomUrn || null

    const [lastInvitationSentUsersHash, set_lastInvitationSentUsersHash] = useState<string | null>(null)

    const onSendInviteClicked = () => {
        if (roomUrn && peerUrns.length > 0) {
            communityFeature.inviteToSessionCall({ userUrns: peerUrns, roomUrn: roomUrn })
        }
        set_lastInvitationSentUsersHash(peerUrns.join(','))
    }

    const onUndoSendInvite = () => {
        if (roomUrn && peerUrns.length > 0) {
            communityFeature.inviteToSessionCancel({ userUrns: peerUrns, roomUrn: roomUrn })
        }
        set_lastInvitationSentUsersHash(null)
    }

    const isButtonDisabled = !roomUrn || lastInvitationSentUsersHash === peerUrns.join(',')

    return (
        <TooltipPopper
            id={id}
            openOnClick
            element={component({ isButtonDisabled, onSendInviteClicked })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            isSticky
        >
            {roomUrn && (
                <div className={gclasses.customTooltipContainer}>
                    <Typography variant="body2" style={{ marginBottom: 8 }}>
                        Session invitation sent!
                    </Typography>
                    <Typography variant="body1" style={{ fontSize: '95%', whiteSpace: 'normal' }}>
                        {peerUsernames.join(', ')} received an invitation to join this room.
                    </Typography>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            className={clsx(
                                gclasses.buttonColorPrimary,
                                gclasses.buttonSmall,
                                gclasses.popupButton,
                                gclasses.buttonNoLoadFlash
                            )}
                            onClick={() => onUndoSendInvite()}
                            disableFocusRipple
                        >
                            Undo
                        </Button>
                    </div>
                </div>
            )}
            {!roomUrn && (
                <div className={gclasses.customTooltipContainer}>
                    <Typography variant="body1" style={{ fontSize: '95%' }}>
                        You need to create or join a room first.
                    </Typography>
                </div>
            )}
        </TooltipPopper>
    )
}

const useStyles = makeStyles((theme) => ({
    popover: {
        // pointerEvents: 'none',
    },
    paper: {
        padding: theme.spacing(1),
        backgroundColor: '#4f4f4f',
        maxWidth: 300,
    },
}))

const TooltipPopper: React.FunctionComponent<{
    id: string
    element: React.ReactNode
    isSticky?: boolean
    isPermanent?: boolean
    openOnClick?: boolean
    enterDelay?: number
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
    children: React.ReactNode
}> = ({
    id,
    element,
    isSticky = false,
    isPermanent = false,
    openOnClick = false,
    enterDelay = 0,
    anchorOrigin,
    transformOrigin,
    children,
}) => {
    const gclasses = useMuseStyles(useTheme())
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = React.useState(null)

    const containerEl = useRef(null)

    const enterDelayTimer = useRef<NodeJS.Timeout | null>(null)
    const tmp = useRef(null)

    const handlePopoverOpen = (event: any) => {
        tmp.current = event.currentTarget
        enterDelayTimer.current = setTimeout(() => {
            setAnchorEl(tmp.current)
        }, enterDelay)
    }

    const handlePopoverClose = () => {
        if (!isPermanent) {
            enterDelayTimer.current && clearTimeout(enterDelayTimer.current)
            setAnchorEl(null)
        }
    }

    const open = Boolean(anchorEl)

    return (
        <>
            <div
                ref={containerEl}
                aria-owns={open ? `${id}-popover` : undefined}
                aria-haspopup="true"
                onMouseEnter={(ev) => {
                    if (!openOnClick) {
                        handlePopoverOpen(ev)
                    }
                }}
                onClick={(ev) => {
                    if (openOnClick) {
                        handlePopoverOpen(ev)
                    }
                }}
                onMouseLeave={() => !isSticky && handlePopoverClose()}
                style={{ display: 'flex' }}
            >
                {element}
            </div>
            <Popover
                id={`${id}-popover`}
                className={clsx(classes.popover, gclasses.customTooltipContainer)}
                classes={{
                    paper: classes.paper,
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={
                    anchorOrigin
                        ? anchorOrigin
                        : {
                              vertical: 'bottom',
                              horizontal: 'left',
                          }
                }
                transformOrigin={
                    transformOrigin
                        ? transformOrigin
                        : {
                              vertical: 'top',
                              horizontal: 'left',
                          }
                }
                onClose={() => handlePopoverClose()}
                onBlur={() => handlePopoverClose()}
                // Fixes #312 [https://github.com/sungeunchoi-kr/muse-app/issues/312]
                container={containerEl.current}
                disableRestoreFocus
            >
                {children}
            </Popover>
        </>
    )
}

const useRoundedSelectStyle = makeStyles((theme) => ({
    selectMenuRoot: {
        boxSizing: 'border-box',
        width: '304px',
        border: '1px solid rgb(63, 63, 63)',
        borderRadius: '20px',
        backgroundColor: '#202122',

        // '& .MuiSelect-iconOpen': {
        //     background: 'red',
        // },
        '& ul': {
            // padding: '1px 0px',
            padding: '0px',
        },

        '& li': {
            color: '#F1F1F1',
            fontFamily: 'Inv Maison Neue,-apple-system,BlinkMacSystemFont,Open Sans,sans-serif',
            fontSize: '14px',
            letterSpacing: 0,
            lineHeight: '18px',
            padding: '8px 27px 8px 24px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            minHeight: 0,

            '& .MuiTypography-root': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },

            // This prevents the ugly default checkbox from appearing on electron modals.
            '& input': {
                display: 'none',
            },
            '& .MuiListItemText-root': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',

                flex: '1 1 auto',
                minWidth: 0,
                marginTop: '4px',
                marginBottom: '4px',
            },
            '& .MuiButtonBase-root': {
                padding: '9px',
            },
        },

        '& li.Mui-selected': {
            color: '#F9CC04',
        },

        '& li.Mui-selected .MuiListItemText-primary': {
            color: '#F9CC04',
        },

        // '&:hover .MuiListItem-root.Mui-selected': {
        //     backgroundColor: "rgb(255,255,255,.1)",
        // },

        '&:hover .MuiListItem-button:hover': {
            backgroundColor: 'rgb(255,255,255,.1)',
        },

        '& .MuiListItem-root.Mui-selected, .MuiListItem-root.Mui-selected:hover': {
            // background-color: rgb(255,255,255,.1);
        },

        '& .MuiListSubheader-root': {
            borderTop: '1px solid #3F3F3F',
            padding: '0',
        },
    },

    icon: {
        color: '#5D5D5D',
        right: '4px',

        '&.MuiSelect-iconOpen': {
            zIndex: 10000,
            color: '#F9CC04',
        },
    },

    roundedInput: {
        '&:hover .MuiSelect-icon': {
            color: '#F9CC04',
        },

        '& .MuiSelect-select.MuiSelect-select': {
            border: '1px solid #5D5D5D',
            borderRadius: '25.5px',
            padding: '8px 24px',
            lineHeight: '18px',
            color: '#5D5D5D',
            fontSize: '14px',

            '&:hover': {
                borderColor: '#F9CC04',
                color: '#F9CC04',
            },

            '&:focus': {
                borderRadius: '25.5px',
            },
        },
    },

    selectedInput: {
        '& .MuiSelect-select.MuiSelect-select': {
            borderColor: '#F9CC04',
            color: '#F9CC04',
        },

        '& .MuiSelect-icon': {
            color: '#F9CC04',
        },
    },
}))

function RoundedSelect(props: { [key: string]: any }) {
    const classes = useRoundedSelectStyle(useTheme())

    const variant = props.multiple ? 'menu' : 'selectedMenu'

    let className = props.value === '_undef' ? classes.roundedInput : clsx(classes.roundedInput, classes.selectedInput)
    // https://material-ui.com/components/popover/#anchor-playground
    return (
        <Select
            {...props}
            className={clsx(className, props.className)}
            disableUnderline
            displayEmpty
            MenuProps={{
                classes: { paper: clsx(classes.selectMenuRoot, props.paper) },
                variant: variant,
                disableScrollLock: true,
            }}
            inputProps={{
                classes: {
                    icon: classes.icon,
                },
            }}
        >
            {' '}
            {props.children}
        </Select>
    )
}

export default CommunityMessagesChatPage
